import { type MouseEvent, useCallback, useEffect, useRef } from 'react';
import NProgress from 'nprogress';

import { safeExecuteProgress } from '../utils';
import useRouter from './useRouter';
import useScopedNavigation from './useScopedNavigation';

import type { TokenScope } from '../constants';

const NAVIGATION_CANCEL_TIMEOUT_MS = 15_000;
let navigationCleanupTimer: ReturnType<typeof setTimeout> | null = null;

const useModifierKeyRef = () => {
  const ref = useRef(false);
  useEffect(() => {
    const onMouseDown = (e: {
      altKey: boolean;
      ctrlKey: boolean;
      metaKey: boolean;
      shiftKey: boolean;
    }) => {
      ref.current = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, []);
  return ref;
};

const mergeSearchParams = (href: string, overrides: Record<string, string>): string => {
  const [pathPart, queryPart] = href.split('?');
  const params = new URLSearchParams(queryPart ?? '');
  Object.entries(overrides).forEach(([k, v]) => params.set(k, v));
  const newQuery = params.toString();
  return newQuery ? `${pathPart}?${newQuery}` : pathPart;
};

interface ScopedLinkCommonOptions {
  /**
   * 반환된 props 가 attach 될 컴포넌트가 anchor (`<a>`, `next/link`,
   * `<MenuItem component="a">` 등) 로 렌더되는지 여부. 기본값 `true`.
   *
   * 보조키(ctrl/cmd/shift/alt)+click 동작이 달라진다.
   * - `true`: 브라우저 네이티브 동작에 양보 (shift→새 창, alt→다운로드, ctrl→새 탭 등).
   * - `false`: `window.open` 으로 새 탭 열기를 emulate. shift→새 창, alt→다운로드,
   *   middle-click 은 emulate 불가 (button 한계).
   *
   * 상세: `docs/docs/hosts/navigation.md`
   */
  isAnchor?: boolean;
  /**
   * **before-click** 사이드이펙트. 매 클릭마다 내부 dispatch (router.push /
   * window.location.href / window.open) 직전에 발화한다.
   *
   * 보조키+click (브라우저 네이티브 새 탭/창) 케이스에서도 호출되므로,
   * 네비게이션 성공 여부와 무관하게 항상 발화하는 사이드이펙트 용도.
   * 예: popover/menu 닫기, 분석 이벤트 기록, 부모 onClick 콜백 호출 등.
   */
  onClick?: () => void;
}

type UseScopedLinkBuilderOptions =
  | (ScopedLinkCommonOptions & {
      /** 콘솔 도메인 외부로의 이동 (사용자 가이드, 공지, 헬프데스크 등). 새 탭에서 열기 */
      external: true;
    })
  | (ScopedLinkCommonOptions & {
      external?: false;
      /**
       * scope 와 무관하게 full-page reload 를 강제한다.
       * 토큰 재발급/서버 데이터 갱신이 필요한 이동(프로젝트 전환, 리전 변경 등)에 사용.
       */
      forceFullPageReload?: boolean;
      /**
       * 대상 path 의 토큰 스코프.
       * 일반적으로 생략하며, 메가메뉴 데이터에서 path 기반으로 자동 추출된다.
       * 특정 케이스에서 자동 추출 결과를 override 해야 할 때만 명시한다.
       */
      scope?: TokenScope;
      /**
       * `getScopedPath` 가 생성한 URL 에서 특정 쿼리 파라미터를 덮어쓴다.
       * 현재 앱 상태(project_id, region 등)와 다른 값으로 이동할 때 사용한다.
       * getScopedPath 호출 후 적용되므로 이중 처리 없이 안전하게 override 된다.
       * @example { [QUERY_KEY.PROJECT_ID]: nextProjectId }
       * @example { [QUERY_KEY.REGION]: nextRegion }
       */
      searchParams?: Record<string, string>;
    });

interface NavLinkProps {
  /**
   * 대상 URL.
   *
   * `<a>`, `next/link`, `<MenuItem component="a">` 처럼 anchor 렌더가 가능한
   * 컴포넌트에 spread 해 사용한다.
   *
   * **anchor 가 아닌 컴포넌트에 spread 하려는 경우 (예: `<button {...linkProps}>`)
   * TS 에러가 발생한다.** 의도가 그러하다면 `getProps(path, { isAnchor: false })`
   * 로 호출하고 반환된 `onClick` 만 사용한다.
   *
   * 상세: `docs/docs/hosts/navigation.md`
   */
  href: string;
  /**
   * 클릭 핸들러 (composite). 호출 순서:
   * 1. `options.onClick` (before-click 사이드이펙트, 있으면)
   * 2. 보조키 분기 (isAnchor 에 따라 bail 또는 window.open emulate)
   * 3. 일반 클릭 → preventDefault (isAnchor=true) 후 분류별 dispatch
   *    (router.push / window.location.href / window.open)
   *
   * anchor 컴포넌트에 spread 한 경우엔 별도 onClick 호출 불필요.
   * anchor 미지원 컴포넌트(DropDownMenu2 등) 에서는 `onClick` 만 전달해 사용.
   */
  onClick: (event?: MouseEvent) => void;
  /** 외부 링크일 때만 반환. anchor 에 spread 한다. */
  rel?: 'noopener noreferrer';
  /** 외부 링크일 때만 반환. anchor 에 spread 한다. */
  target?: '_blank';
}

/**
 * 콘솔 내 화면 이동을 위한 통합 네비게이션 props 팩토리 훅.
 *
 * @description
 * - 베이스 훅(`useRouter`, `useScopedNavigation`)을 1회만 호출하고, 반환된
 *   `getProps(path, options)` 로 링크별 props 를 생성한다.
 * - 같은 컴포넌트 안에서 여러 개의 링크를 만들 때 `Rules of Hooks` 에 위배되지 않으면서
 *   sub component 추출 없이 인라인 사용이 가능하다.
 * - 이동 경로 분류(External / Internal · same-scope / Internal · cross-scope) 에 따라
 *   `<a>` / `<Link>` 에 spread 가능한 props 를 반환한다.
 * - 분류와 동작 규칙은 `docs/docs/hosts/navigation.md` 참고.
 *
 * @example 단일 링크
 * ```tsx
 * const { getProps } = useScopedLinkBuilder();
 * <a {...getProps('/vpc/instances')}>VPC 인스턴스</a>
 * ```
 *
 * @example 리스트 인라인
 * ```tsx
 * const { getProps } = useScopedLinkBuilder();
 * {services.map(service => (
 *   <a key={service.path} {...getProps(service.path)}>{service.name}</a>
 * ))}
 * ```
 *
 * @example External 링크
 * ```tsx
 * const { getProps } = useScopedLinkBuilder();
 * <MenuItem component="a" {...getProps(EXTERNAL_LINK.HELP_DESK.NOTICE, { external: true })}>
 *   공지사항
 * </MenuItem>
 * ```
 *
 * @example 프로젝트/리전 전환 (토큰 재발급 필요)
 * ```tsx
 * const { getProps } = useScopedLinkBuilder();
 * <a {...getProps('/dashboard', { forceFullPageReload: true, searchParams: { [QUERY_KEY.PROJECT_ID]: nextProjectId } })}>
 *   {projectName}
 * </a>
 * ```
 *
 * @example DropDownMenu2 등 anchor 미지원 컴포넌트
 * ```tsx
 * const { getProps } = useScopedLinkBuilder();
 * const linkProps = getProps('/iam/members', { isAnchor: false });
 * // TODO(navigation): DropdownMenu2 anchor 지원 시 href 로 전환.
 * <DropDownMenu2 items={[{ text: '멤버 관리', onClick: linkProps.onClick }]} />
 * ```
 */
export const useScopedLinkBuilder = () => {
  const { push } = useRouter();
  const { getScopedPath, getTargetScope, getIsSameScopeWithCurrent } = useScopedNavigation();
  const modifierKeyRef = useModifierKeyRef();

  /**
   * 현재 탭에서 full-page reload를 실행하며 화면 상단에 progress bar를 노출
   */
  const navigateWithFullPageReload = useCallback((href: string) => {
    if (navigationCleanupTimer !== null) {
      clearTimeout(navigationCleanupTimer);
    }

    if (!NProgress.isStarted()) {
      safeExecuteProgress(() => NProgress.start());
    }

    navigationCleanupTimer = setTimeout(() => {
      navigationCleanupTimer = null;
      safeExecuteProgress(() => NProgress.done(false));
    }, NAVIGATION_CANCEL_TIMEOUT_MS);

    window.location.href = href;
  }, []);

  const getProps = useCallback(
    (path: string, options?: UseScopedLinkBuilderOptions): NavLinkProps => {
      const isExternal = options?.external === true;
      const isAnchor = options?.isAnchor !== false;
      const explicitScope = !isExternal ? options?.scope : undefined;
      const forceFullPageReload = !isExternal && options?.forceFullPageReload === true;
      const userOnClick = options?.onClick;
      const searchParamsOverrides = !isExternal ? options?.searchParams : undefined;

      const baseHref = isExternal
        ? path
        : getScopedPath(path, explicitScope ?? getTargetScope(path));
      const href = searchParamsOverrides
        ? mergeSearchParams(baseHref, searchParamsOverrides)
        : baseHref;

      const onClick = (event?: MouseEvent) => {
        // before-click 사이드이펙트: 매 클릭마다 dispatch 직전 발화.
        // modifier+click (bail) 케이스에서도 항상 호출된다.
        userOnClick?.();

        const hasModifier = modifierKeyRef.current;

        if (hasModifier) {
          if (isAnchor) {
            // anchor: 브라우저 네이티브 동작에 양보 (shift→새 창, ctrl→새 탭 등)
            return;
          }
          // non-anchor: 새 탭 열기 emulate (best-effort).
          // default action 이 없으므로 preventDefault 불필요.
          window.open(href, '_blank', 'noopener,noreferrer');
          return;
        }

        if (isAnchor) {
          // anchor 의 default navigation 과 JS dispatch 가 중복되지 않도록 preventDefault.
          // 호출자가 isAnchor 를 정확히 명시했다는 contract 에 의존한다.
          event?.preventDefault();
        }

        if (isExternal) {
          window.open(href, '_blank', 'noopener,noreferrer');
          return;
        }

        if (forceFullPageReload) {
          navigateWithFullPageReload(href);
          return;
        }

        const targetScope = explicitScope ?? getTargetScope(path);
        if (getIsSameScopeWithCurrent(targetScope)) {
          push(href);
        } else {
          navigateWithFullPageReload(href);
        }
      };

      if (isExternal) {
        return {
          href,
          target: '_blank',
          rel: 'noopener noreferrer',
          onClick,
        };
      }

      return { href, onClick };
    },
    [
      push,
      getScopedPath,
      getTargetScope,
      getIsSameScopeWithCurrent,
      navigateWithFullPageReload,
      modifierKeyRef,
    ],
  );

  return { getProps };
};

export type { UseScopedLinkBuilderOptions, NavLinkProps };
